import { useCallback, useRef, useState } from 'react';
import ImageHandler from '../utils/ImageHandler';
import {
  canAddComponentToScreen,
  getAddBlockerMessage,
  getComponentDefaultProperties,
} from '../utils/componentUtils';
import { getFooterIndex } from '../utils/footerManager';

/**
 * Enables dropping image files straight from the OS onto the canvas to create
 * Image components.
 *
 * This is native HTML5 file drag-and-drop (event.dataTransfer.files), which is
 * a separate mechanism from @dnd-kit (which only handles in-app draggables like
 * palette -> canvas). The two coexist: dnd-kit's PointerSensor ignores native
 * file drags, so wiring these DOM handlers onto the canvas does not interfere
 * with component reordering.
 *
 * @param {object}   args
 * @param {object}   args.screen        Active screen (for limit checks + insert index)
 * @param {Function} args.addComponent  (type, defaultProps, insertIndex) => void
 * @param {Function} args.onError       (message) => void  surfaced via snackbar
 * @returns {{ isDraggingFile: boolean, dropHandlers: object }}
 */
export const useImageFileDrop = ({ screen, addComponent, onError }) => {
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  // dragenter/dragleave fire for every child element; count depth so the
  // overlay does not flicker as the cursor moves over nested nodes.
  const dragDepth = useRef(0);

  const hasFiles = (event) =>
    Array.from(event.dataTransfer?.types || []).includes('Files');

  const handleDragEnter = useCallback((event) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setIsDraggingFile(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    if (!hasFiles(event)) return;
    // Required so the browser fires a drop event rather than navigating to the file.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = useCallback((event) => {
    if (!hasFiles(event)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) {
      setIsDraggingFile(false);
    }
  }, []);

  const handleDrop = useCallback(
    async (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepth.current = 0;
      setIsDraggingFile(false);

      const imageFiles = Array.from(event.dataTransfer.files || []).filter((file) =>
        file.type.startsWith('image/')
      );

      if (imageFiles.length === 0) {
        onError?.('Only image files (JPEG, PNG) can be dropped onto the canvas.');
        return;
      }

      for (const file of imageFiles) {
        // Re-check the limit before each insert; addComponent has already
        // mutated the screen for previously dropped files in this batch.
        if (!canAddComponentToScreen('Image', screen)) {
          onError?.(getAddBlockerMessage('Image', screen));
          break;
        }

        try {
          // convertFile validates type/size (JPEG/PNG, max 300KB) and throws otherwise.
          const rawBase64 = await ImageHandler.convertFile(file);
          const insertIndex = computeInsertIndex(screen);
          addComponent('Image', {
            ...getComponentDefaultProperties('Image'),
            src: rawBase64,
            'alt-text': stripExtension(file.name),
          }, insertIndex);
        } catch (error) {
          onError?.(error.message || `Could not add ${file.name}`);
        }
      }
    },
    [screen, addComponent, onError]
  );

  return {
    isDraggingFile,
    dropHandlers: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
};

// New components land at the end of the screen, but always before the Footer.
function computeInsertIndex(screen) {
  const end = screen?.components?.length ?? 0;
  const footerIdx = getFooterIndex(screen);
  return footerIdx !== -1 ? Math.min(end, footerIdx) : end;
}

function stripExtension(fileName) {
  return fileName.replace(/\.[^.]+$/, '');
}

export default useImageFileDrop;
